import React, { useState } from 'react';
import { validateEmail } from '../../utils/validation';
import { Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';

// Configuração da API
const API_URL = process.env.NODE_ENV === 'production' 
  ? 'https://dashsimples.vercel.app/api'
  : 'http://localhost:3001/api';

const ForgotPasswordModal = ({ isOpen, onClose, onBackToLogin }) => {
  const [step, setStep] = useState(1); // 1: email, 2: código, 3: nova senha
  const [formData, setFormData] = useState({
    email: '',
    code: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    hasLower: false,
    hasUpper: false,
    hasNumber: false,
    hasSpecial: false,
    hasLength: false
  });

  const checkPasswordStrength = (password) => {
    const strength = {
      hasLower: /[a-z]/.test(password),
      hasUpper: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[@$!%*?&]/.test(password),
      hasLength: password.length >= 8
    };
    setPasswordStrength(strength);
    return strength;
  };

  const isPasswordStrong = () => {
    return Object.values(passwordStrength).every(Boolean);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'password') {
      checkPasswordStrength(value);
    }

    if (message) setMessage('');
  };

  // Etapa 1: Enviar email
  const handleSendEmail = async (e) => {
    e.preventDefault();
    
    if (!validateEmail(formData.email)) {
      setMessage('Por favor, insira um email válido.');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formData.email.toLowerCase().trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        setStep(2);
        setMessage('Código enviado para seu email!');
      } else {
        setMessage(data.error || 'Erro ao processar solicitação. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao solicitar reset de senha:', error);
      setMessage('Erro de conexão. Verifique sua internet e tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // Etapa 2: Validar código
  const handleValidateCode = async (e) => {
    e.preventDefault();
    
    if (!formData.code || formData.code.length !== 6) {
      setMessage('Por favor, insira o código de 6 dígitos.');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const response = await fetch('http://localhost:3001/api/auth/validate-reset-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: formData.email, 
          code: formData.code 
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setStep(3);
        setMessage('Código validado! Agora defina sua nova senha.');
      } else {
        setMessage(data.error || 'Código inválido. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao validar código:', error);
      setMessage('Erro de conexão. Verifique sua internet e tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // Etapa 3: Redefinir senha
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!isPasswordStrong()) {
      setMessage('A senha deve atender a todos os critérios de segurança.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setMessage('As senhas não coincidem.');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      // Primeiro, obter token CSRF
      const csrfResponse = await fetch(`${API_URL}/csrf-token`, {
        method: 'GET',
        credentials: 'include'
      });
      
      let csrfToken = '';
      if (csrfResponse.ok) {
        const csrfData = await csrfResponse.json();
        csrfToken = csrfData.data?.token || '';
      }

      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({
          email: formData.email,
          code: formData.code,
          newPassword: formData.password,
          confirmPassword: formData.confirmPassword
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSuccess(true);
        setMessage('Senha redefinida com sucesso!');
      } else {
        setMessage(data.error || 'Erro ao redefinir senha. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao redefinir senha:', error);
      setMessage('Erro de conexão. Verifique sua internet e tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const PasswordCriteria = ({ met, text }) => (
    <div className={`flex items-center text-xs ${met ? 'text-green-600' : 'text-gray-400'}`}>
      <CheckCircle className={`w-3 h-3 mr-1 ${met ? 'text-green-500' : 'text-gray-300'}`} />
      {text}
    </div>
  );

  const handleClose = () => {
    setStep(1);
    setFormData({
      email: '',
      code: '',
      password: '',
      confirmPassword: ''
    });
    setMessage('');
    setIsSuccess(false);
    onClose();
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setMessage('');
    }
  };

  if (!isOpen) return null;

  const getStepTitle = () => {
    switch (step) {
      case 1: return 'Redefinir Senha';
      case 2: return 'Digite o Código';
      case 3: return 'Nova Senha';
      default: return 'Redefinir Senha';
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 1: return 'Digite seu email para receber o código de redefinição';
      case 2: return 'Digite o código de 6 dígitos enviado para seu email';
      case 3: return 'Defina sua nova senha segura';
      default: return '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {getStepTitle()}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {getStepDescription()}
          </p>
          
          {/* Progress indicator */}
          <div className="flex mt-4 space-x-2">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-2 flex-1 rounded-full ${
                  s <= step ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {!isSuccess ? (
            <>
              {step === 1 && (
                <form onSubmit={handleSendEmail} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="seu@email.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      required
                      autoFocus
                    />
                  </div>

                  {message && (
                    <div className="p-3 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                      {message}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Enviando...
                      </>
                    ) : (
                      'Enviar Código'
                    )}
                  </button>
                </form>
              )}

              {step === 2 && (
                <form onSubmit={handleValidateCode} className="space-y-4">
                  {message && message.includes('Código enviado') && (
                    <div className="p-3 rounded-lg text-sm bg-blue-50 text-blue-700 border border-blue-200 flex items-center mb-4">
                      <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                      {message}
                    </div>
                  )}

                  <div>
                    <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                      Código de Verificação
                    </label>
                    <input
                      type="text"
                      id="code"
                      name="code"
                      value={formData.code}
                      onChange={handleChange}
                      placeholder="Digite o código de 6 dígitos"
                      maxLength="6"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-center text-lg tracking-widest font-mono"
                      required
                      autoFocus
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Código enviado para <strong>{formData.email}</strong>
                    </p>
                  </div>

                  {message && !message.includes('Código enviado') && (
                    <div className={`p-3 rounded-lg text-sm flex items-center ${
                      message.includes('validado') 
                        ? 'bg-green-50 text-green-700 border border-green-200' 
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      {message.includes('validado') ? (
                        <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                      )}
                      {message}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Validando...
                      </>
                    ) : (
                      'Validar Código'
                    )}
                  </button>
                </form>
              )}

              {step === 3 && (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  {message && message.includes('validado') && (
                    <div className="p-3 rounded-lg text-sm bg-green-50 text-green-700 border border-green-200 flex items-center mb-4">
                      <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                      {message}
                    </div>
                  )}

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                      Nova Senha
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Sua nova senha"
                        className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                          isPasswordStrong()
                            ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                            : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                        required
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>

                    {formData.password && (
                      <div className="mt-2 space-y-1">
                        <PasswordCriteria met={passwordStrength.hasLength} text="Pelo menos 8 caracteres" />
                        <PasswordCriteria met={passwordStrength.hasLower} text="Uma letra minúscula" />
                        <PasswordCriteria met={passwordStrength.hasUpper} text="Uma letra maiúscula" />
                        <PasswordCriteria met={passwordStrength.hasNumber} text="Um número" />
                        <PasswordCriteria met={passwordStrength.hasSpecial} text="Um caractere especial (@$!%*?&)" />
                      </div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      Confirmar Nova Senha
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="Confirme sua nova senha"
                        className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                          formData.confirmPassword && formData.password === formData.confirmPassword
                            ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                            : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>

                    {formData.confirmPassword && formData.password === formData.confirmPassword && (
                      <div className="mt-2 flex items-center text-green-600">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        <span className="text-sm">Senhas coincidem ✓</span>
                      </div>
                    )}
                  </div>

                  {message && !message.includes('validado') && (
                    <div className="p-3 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                      {message}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading || !isPasswordStrong() || formData.password !== formData.confirmPassword}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Redefinindo...
                      </>
                    ) : (
                      'Redefinir Senha'
                    )}
                  </button>
                </form>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Senha Redefinida!
              </h3>
              
              <p className="text-gray-600 text-sm mb-6">
                {message} Você pode fazer login com sua nova senha.
              </p>

              <button
                onClick={() => {
                  handleClose();
                  onBackToLogin();
                }}
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                Fazer Login
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {!isSuccess && step < 3 && (
          <div className="px-6 py-4 bg-gray-50 rounded-b-xl">
            <div className="flex justify-start">
              <button
                onClick={step > 1 ? handleBack : onBackToLogin}
                className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                ← {step > 1 ? 'Voltar' : 'Voltar ao Login'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
